import { z } from 'zod';

import { type ArtifactStatus, type DraftArtifactKind } from './artifacts.ts';
import type { StudyState } from './lifecycle.ts';
import type { IntakeKind } from './materials-shape.ts';
import type { StudyTypeSelection } from './study-type-selection.ts';
import { categoryById, studyTypeName } from './study-types.ts';

/**
 * The ten steps a study is walked through, in order.
 *
 * The slugs are one identifier, used by the stored `studies.current_step` column, by its check
 * constraint and by the URL segment that addresses a step, so there is nothing to translate
 * between the three and no way for them to disagree.
 */
export const STUDY_STEP_SLUGS = [
  'study_type',
  'materials',
  'goal',
  'questions',
  'guide',
  'audience',
  'launch',
  'fielding',
  'analyzing',
  'brief',
] as const;

export const studyStepSlugSchema = z.enum(STUDY_STEP_SLUGS);
export type StudyStepSlug = z.infer<typeof studyStepSlugSchema>;

export const STUDY_STEP_COUNT = STUDY_STEP_SLUGS.length;

/**
 * The steps that run once the study is launched, as against the ones that set it up: nothing on
 * them is the researcher's to write, and they follow Launch to the end of the ten.
 */
export const RUNNING_STUDY_STEP_SLUGS = [
  'fielding',
  'analyzing',
  'brief',
] as const satisfies readonly StudyStepSlug[];

export const isRunningStudyStep = (slug: StudyStepSlug): boolean =>
  (RUNNING_STUDY_STEP_SLUGS as readonly StudyStepSlug[]).includes(slug);

/** The step a study starts on, and the column's default. */
export const FIRST_STUDY_STEP: StudyStepSlug = 'study_type';

export interface StudyStep {
  slug: StudyStepSlug;
  /** What the step is called, on its own screen and in the Study so far sheet. */
  name: string;
  /**
   * What the step is for, in one sentence: what the agent is told of the step its thread belongs
   * to. Bundled rather than on the wire, since no screen shows it.
   */
  why: string;
  /** What happens on the step, in one sentence: what the rail says of a step not on screen. */
  about: string;
  /**
   * Whether this step has a screen of its own to show.
   *
   * A step that does not is not missing from the list and is not skipped: it counts towards the
   * ten, it is navigable once the study has reached it, and it is never reported complete whatever
   * the study has done — so no study can ever present it as work that was completed. Completion is
   * a property this flag grants and nothing else can confer.
   */
  implemented: boolean;
  /**
   * The conversation thread this step's screen shows beside it.
   *
   * A step with a thread of its own names itself. Analyzing has none and names Fielding's, so the
   * move from Fielding keeps the same conversation on screen. A property of the registry rather
   * than a condition in the shell, so the shell holds no second list of slugs that can drift from
   * this one.
   */
  thread: ChatStepSlug;
}

/**
 * The ten steps, with the order they happen in.
 *
 * Ordered by the array itself rather than by an index field: a number beside each entry would be
 * a second statement of the same fact, free to drift from the order the list is written in.
 */
export const STUDY_STEPS: readonly StudyStep[] = [
  {
    slug: 'study_type',
    name: 'Study type',
    why: 'Every study starts from a proven shape, so picking the closest one is what lets Sigora fill in the rest.',
    about:
      'Pick the kind of study closest to what you want to learn, and Sigora shapes the rest around it.',
    implemented: true,
    thread: 'study_type',
  },
  {
    slug: 'materials',
    name: 'Materials',
    why: 'What you already have is where the study’s facts come from, so Sigora reads it and shows you what it took.',
    about: 'Add what you already have about the product, and check the facts Sigora reads from it.',
    implemented: true,
    thread: 'materials',
  },
  {
    slug: 'goal',
    name: 'Goal',
    why: 'A study answers one thing well, and naming that thing is what the questions, the guide and the verdict are all built from.',
    about: 'Choose the one thing this study should find out.',
    implemented: true,
    thread: 'goal',
  },
  {
    slug: 'questions',
    name: 'Questions',
    why: 'These are what the study will answer, so they are worth reading before anybody is asked anything.',
    about: 'Read the research questions the study will answer, and approve them.',
    implemented: true,
    thread: 'questions',
  },
  {
    slug: 'guide',
    name: 'Guide',
    why: 'This is what people will actually see, and how long it takes them decides both the price and how many finish.',
    about: 'Review the conversation people will take part in, question by question.',
    implemented: true,
    thread: 'guide',
  },
  {
    slug: 'audience',
    name: 'Audience',
    why: 'Who answers decides what the answers mean, so the filters here are the honest contract of what you are buying.',
    about: 'Decide who takes part, how many of them, and where they live.',
    implemented: true,
    thread: 'audience',
  },
  {
    slug: 'launch',
    name: 'Launch',
    why: 'Paying fixes the study as it stands, so this is the last point at which anything in it can change.',
    about: 'See the price, pay, and Sigora sets the study up and sends it out to participants.',
    implemented: true,
    thread: 'launch',
  },
  {
    slug: 'fielding',
    name: 'Fielding',
    why: 'Real people are answering now, and nothing here needs you until their answers are in.',
    about:
      'Participants are answering, and you can watch the count of finished conversations rise.',
    implemented: true,
    thread: 'fielding',
  },
  {
    /*
      A step, not the `analyzing` study state, though it shares the spelling: it is reached on that
      state and stays reached once the study is `complete`.
    */
    slug: 'analyzing',
    name: 'Analyzing',
    why: 'Sigora reads every answer and writes your brief. The study moves on to it by itself.',
    about: 'Sigora is reading the answers and writing your brief.',
    implemented: true,
    // The answers being read and the brief being written. It has no thread of its own.
    thread: 'fielding',
  },
  {
    slug: 'brief',
    name: 'Brief',
    why: 'One page with a verdict your stakeholders can read, and the evidence behind it.',
    about: 'Read the brief, ask Sigora about it, and share it with your stakeholders.',
    implemented: true,
    thread: 'brief',
  },
];

/**
 * The steps with a conversation thread of their own, in registry order: the steps a study's chat
 * threads are named by.
 *
 * Written out rather than read from `STUDY_STEPS` so the stored `step` columns' check constraints
 * and the types built on this list see literal slugs; the registry's `thread` fields and this list
 * are held together by a unit test, as `STUDY_STEP_SLUGS` and the registry are.
 */
export const CHAT_STUDY_STEP_SLUGS = [
  'study_type',
  'materials',
  'goal',
  'questions',
  'guide',
  'audience',
  'launch',
  'fielding',
  'brief',
] as const satisfies readonly StudyStepSlug[];

/**
 * A chat step: the name of one of a study's conversation threads.
 *
 * A thread is a study and one of these, and nothing else names one — the stored `step` columns,
 * the thread routes and a turn's own row all carry a value this schema admits.
 */
export const chatStepSlugSchema = z.enum(CHAT_STUDY_STEP_SLUGS);
export type ChatStepSlug = z.infer<typeof chatStepSlugSchema>;

export const isChatStepSlug = (value: unknown): value is ChatStepSlug =>
  chatStepSlugSchema.safeParse(value).success;

/** The thread a step's screen shows: the registry's `thread` for that step. */
export const threadStepOf = (slug: StudyStepSlug): ChatStepSlug => studyStep(slug).thread;

const INDEX_BY_SLUG = new Map(STUDY_STEPS.map((step, index) => [step.slug, index]));

/** Where a step sits in the ten, counted from zero. */
export const studyStepIndex = (slug: StudyStepSlug): number => INDEX_BY_SLUG.get(slug) as number;

/** What a person counts: "Step n of 10". */
export const studyStepNumber = (slug: StudyStepSlug): number => studyStepIndex(slug) + 1;

export const studyStep = (slug: StudyStepSlug): StudyStep =>
  STUDY_STEPS[studyStepIndex(slug)] as StudyStep;

export const isStudyStepSlug = (value: string): value is StudyStepSlug =>
  INDEX_BY_SLUG.has(value as StudyStepSlug);

/**
 * The step after this one, or null at the end of the ten.
 */
export const nextStudyStep = (slug: StudyStepSlug): StudyStepSlug | null =>
  STUDY_STEPS[studyStepIndex(slug) + 1]?.slug ?? null;

/** The step before this one, or null at the start. */
export const previousStudyStep = (slug: StudyStepSlug): StudyStepSlug | null => {
  const index = studyStepIndex(slug);

  return index === 0 ? null : (STUDY_STEPS[index - 1] as StudyStep).slug;
};

/**
 * Where a step stands for one study.
 *
 * `unimplemented` is a status rather than an absence, and it outranks every answer but `locked`: a
 * step the product has not built cannot be reported as complete however far the study has got,
 * and one the study has not reached is locked like any other.
 * `complete` is work that is done, `current` is where the study is, `available` is a step behind
 * the study that can be returned to, and `locked` is one the study has not reached — which is what
 * a deep link to it is replaced over, since a bookmark pointing ahead of the work is stale rather
 * than wrong.
 *
 * Post-launch read-only is deliberately *not* one of these. Whether an input can still change is a
 * property of the study's lock, which every reader of this already holds, and folding it in here
 * would cost the sheet the approved summaries it exists to show.
 */
export const STUDY_STEP_STATUSES = [
  'complete',
  'current',
  'available',
  'locked',
  'unimplemented',
] as const;
export const studyStepStatusSchema = z.enum(STUDY_STEP_STATUSES);
export type StudyStepStatus = z.infer<typeof studyStepStatusSchema>;

/**
 * One step as the study read reports it. A schema rather than a bare type because it crosses the
 * wire, and the SPA parses every response it is given.
 */
export const studyStepViewSchema = z.object({
  slug: studyStepSlugSchema,
  name: z.string(),
  about: z.string(),
  status: studyStepStatusSchema,
  /**
   * One line of what this step settled, or null when it has settled nothing. Never populated for
   * an unimplemented step: a step with no screen has produced nothing to summarise, and a line
   * here would be the sheet claiming otherwise.
   */
  summary: z.string().nullable(),
});
export type StudyStepView = z.infer<typeof studyStepViewSchema>;

/** One draft artifact's standing, as much of it as a step summary needs. */
export interface StudyStepArtifact {
  kind: DraftArtifactKind;
  currentVersion: number;
  approvedVersion: number | null;
  status: ArtifactStatus;
}

export interface StudyStepInput {
  /** Where the study remembers it was. */
  currentStep: StudyStepSlug;
  state: StudyState;
  /**
   * Whether the study's documents have been frozen. Set partway through the set-up run, before
   * Remesh has confirmed anything, so it says the study can no longer be edited and nothing more.
   */
  locked: boolean;
  /** Whether the study has been paid for, which closes its documents before the freeze does. */
  paid: boolean;
  /**
   * Whether Remesh confirmed the launch. The only one of the three that means the study is
   * actually out in the world; the other two are money and a freeze, both of which happen first.
   */
  launched: boolean;
  /**
   * Which Study Type the study is, or nothing while nobody has chosen one.
   *
   * The study's own answer rather than the catalogue's current one: it carries the template
   * version the study is pinned to, so a step reading it reads what that study was built against.
   */
  studyType: StudyTypeSelection | null;
  artifacts: readonly StudyStepArtifact[];
  /** What the study's Materials say, or nothing while none have been saved. */
  materials: StudyStepMaterials | null;
  /**
   * The study's Goal, or nothing while none has been chosen. Absent from a caller that read nothing
   * about the Goal, which reads the same as none chosen.
   */
  goal?: StudyStepGoal | null | undefined;
  /**
   * The study's research questions, or nothing while it has none. Absent from a caller that read
   * nothing about them, which leaves their summary without a count.
   */
  questions?: StudyStepQuestions | null | undefined;
  /**
   * The study's guide, or nothing while it has none. Absent from a caller that read nothing about
   * it, which leaves its summary without a count or minutes.
   */
  guide?: StudyStepGuide | null | undefined;
  /**
   * The study's audience, or nothing while it has none. Absent from a caller that read nothing
   * about it, which leaves its summary without a count.
   */
  audience?: StudyStepAudience | null | undefined;
}

/** As much of a study's audience as the step's summary reads beyond its standing. */
export interface StudyStepAudience {
  /** How many people the current version asks for. */
  count: number;
}

/** As much of a study's research questions as the step's summary reads beyond their standing. */
export interface StudyStepQuestions {
  /** How many questions the current version holds. */
  count: number;
}

/** As much of a study's guide as the step's summary reads beyond its standing. */
export interface StudyStepGuide {
  /** How many questions the current version holds. */
  count: number;
  /** The minutes it asks of each participant, as a person reads them; see `displayMinutes`. */
  minutes: number;
}

/** As much of a study's Goal as the step's completion and summary read. */
export interface StudyStepGoal {
  /** Whether the Goal is approved at the version it holds now, with nothing outstanding. */
  chosenCurrent: boolean;
  /** The chosen option's title and the Goal's version, as the Study so far sheet shows it. */
  summary: string;
}

/** As much of a study's Materials as the step's completion and summary read. */
export interface StudyStepMaterials {
  /**
   * Whether the version the study holds now is confirmed, for the Study Type it has now — the
   * answer `materialsConfirmedCurrent` gives, and no other.
   */
  confirmedCurrent: boolean;
  /** How many of the rows hold a value. */
  factCount: number;
  /** The intake the current version was captured through. */
  intake: IntakeKind;
}

/**
 * Which artifact each document-backed step reports on, or nothing for a step that has none.
 *
 * Exported because two sides need the same answer and only one of them can own it: this file
 * decides whether such a step is complete and what its summary says, and the SPA decides which
 * document its screen renders. A second copy over there would be a step added here that the
 * screen silently did not know about — reported complete, drawn as something else.
 */
export const studyStepArtifact = (slug: StudyStepSlug): DraftArtifactKind | undefined =>
  ARTIFACT_BY_STEP[slug];

const ARTIFACT_BY_STEP: Partial<Record<StudyStepSlug, DraftArtifactKind>> = {
  questions: 'rqs',
  guide: 'dg',
  audience: 'audience',
};

const STEP_BY_ARTIFACT: Record<DraftArtifactKind, ChatStepSlug> = {
  rqs: 'questions',
  dg: 'guide',
  audience: 'audience',
};

/**
 * The step whose screen carries a draft document, which is the thread a turn about that document
 * belongs in — the inverse of `studyStepArtifact`, and held to it by a unit test.
 *
 * An approval is made on that step's card, so the turn it starts answers the click in the thread
 * the researcher is looking at.
 */
export const stepOfDraftKind = (kind: DraftArtifactKind): ChatStepSlug => STEP_BY_ARTIFACT[kind];

/**
 * The states in which the Analyzing step is reached: the study is being analysed, or was, and its
 * Brief is written. Fielding that ended any other way was never analysed.
 */
const ANALYZING_STATES = new Set<StudyState>(['analyzing', 'complete']);

/** The states in which fielding is over, whatever the ending was. */
const FIELDING_ENDED = new Set<StudyState>([
  'analyzing',
  'complete',
  'under_recruited',
  'cancelled',
]);

/**
 * Whether a step stays locked whatever the cursor and the steps before it say, because it reads
 * something only a state the study is in can give it. The Brief is read only once it is published,
 * which is exactly when the study is `complete`; Analyzing shows the reading of the answers, which
 * only an analysed study has.
 */
const isHeldByState = (slug: StudyStepSlug, state: StudyState): boolean =>
  (slug === 'brief' && state !== 'complete') ||
  (slug === 'analyzing' && !ANALYZING_STATES.has(state));

/**
 * The Fielding step's summary in Study so far, by state. Decided by the state alone: a `live` study
 * is people answering whatever its count, since only the server decides fielding is over.
 */
const FIELDING_SUMMARIES: Partial<Record<StudyState, string>> = {
  live: 'People are answering',
  analyzing: 'Answers are in',
  complete: 'Answers are in',
  under_recruited: 'Stopped short of the quality floor',
  cancelled: 'Cancelled',
};

/** The Analyzing step's summary in Study so far, by state: only the two states that reach it. */
const ANALYZING_SUMMARIES: Partial<Record<StudyState, string>> = {
  analyzing: 'Reading the answers',
  complete: 'Brief written',
};

const artifactSummary = (artifact: StudyStepArtifact | undefined): string | null => {
  if (artifact === undefined) {
    return null;
  }

  if (artifact.status === 'approved' && artifact.approvedVersion !== null) {
    return `Version ${artifact.approvedVersion}, approved`;
  }

  if (artifact.status === 'stale') {
    return `Version ${artifact.currentVersion}, waiting on a change above it`;
  }

  return `Version ${artifact.currentVersion}, not approved yet`;
};

/** The research questions' line: how many there are, then where the version stands. */
const questionsSummary = (
  artifact: StudyStepArtifact | undefined,
  questions: StudyStepQuestions | null | undefined,
): string | null => {
  const standing = artifactSummary(artifact);
  if (standing === null || questions === null || questions === undefined) {
    return standing;
  }

  const count = `${String(questions.count)} ${questions.count === 1 ? 'question' : 'questions'}`;

  return `${count}, ${standing}`;
};

/** The guide's line: how many questions and how long, then where the version stands. Placeholder copy. */
const guideSummary = (
  artifact: StudyStepArtifact | undefined,
  guide: StudyStepGuide | null | undefined,
): string | null => {
  const standing = artifactSummary(artifact);
  if (standing === null || guide === null || guide === undefined) {
    return standing;
  }

  const count = `${String(guide.count)} ${guide.count === 1 ? 'question' : 'questions'}`;
  const minutes = `about ${String(guide.minutes)} ${guide.minutes === 1 ? 'minute' : 'minutes'}`;

  return `${count}, ${minutes}, ${standing}`;
};

/** The audience's line: how many people, then where the version stands. Placeholder copy. */
const audienceSummary = (
  artifact: StudyStepArtifact | undefined,
  audience: StudyStepAudience | null | undefined,
): string | null => {
  const standing = artifactSummary(artifact);
  if (standing === null || audience === null || audience === undefined) {
    return standing;
  }

  return `${String(audience.count)} ${audience.count === 1 ? 'person' : 'people'}, ${standing}`;
};

/**
 * What the launch step has settled.
 *
 * The three facts happen in order and none of them implies the next: a study is paid for at
 * checkout, frozen partway through the set-up run, and launched only once Remesh confirms. A
 * study that is frozen has not launched, so only `launched` may say so — the same distinction the
 * study list draws when it calls a locked, unlaunched study "Setting up".
 */
const launchSummary = (input: StudyStepInput): string | null => {
  if (input.launched) {
    return 'Paid and launched';
  }

  if (input.paid || input.locked) {
    return 'Paid, being set up';
  }

  return input.state === 'ready' ? 'Ready to launch' : null;
};

/**
 * What a study's chosen type reads as on the sheet, or nothing while nothing is chosen.
 *
 * The Category is named only for Custom, which is the one type whose own name says nothing about
 * what the study is about; a named type carries its Category in the catalogue, where a reader who
 * wants it can see it beside the type itself.
 *
 * A type this build's catalogue cannot name reads as nothing rather than as its stored id. The
 * study read refuses such a row long before this runs, so the branch is the same answer given
 * twice — and an id on the sheet would be the one form of this that looked like display copy.
 */
const studyTypeSummary = (selection: StudyTypeSelection | null): string | null => {
  if (selection === null) {
    return null;
  }

  const name = studyTypeName(selection.id);
  if (name === undefined) {
    return null;
  }

  const category = selection.category === null ? undefined : categoryById(selection.category);

  return category === undefined ? name : `${name} · ${category.name}`;
};

/**
 * How each intake reads on the sheet: what the researcher gave, in the words they would use.
 */
const INTAKE_SUMMARIES: Record<IntakeKind, string> = {
  text: 'A description',
  form: 'A form',
  paired: 'Two versions',
};

/**
 * What a study's Materials read as on the sheet, or nothing while none have been saved.
 *
 * The count is of facts confirmed, so it is said only of a confirmation that stands: Materials that
 * were saved and not confirmed, or confirmed at a version or a Study Type the study has since
 * moved on from, say so instead of counting facts nobody has agreed to.
 */
const materialsSummary = (materials: StudyStepMaterials | null): string | null => {
  if (materials === null) {
    return null;
  }

  const given = INTAKE_SUMMARIES[materials.intake];
  if (!materials.confirmedCurrent) {
    return `${given}, not confirmed yet`;
  }

  return `${given}, ${String(materials.factCount)} ${materials.factCount === 1 ? 'fact' : 'facts'} confirmed`;
};

/**
 * Whether the work a step stands for has been done.
 *
 * Exported with `studyStepSummary` because a step's completion and summary are decided whether or
 * not its screen is built, while `deriveStudySteps` reports neither for an unbuilt one.
 */
export const isStudyStepComplete = (slug: StudyStepSlug, input: StudyStepInput): boolean => {
  const kind = studyStepArtifact(slug);
  if (kind !== undefined) {
    return input.artifacts.find((artifact) => artifact.kind === kind)?.status === 'approved';
  }

  if (slug === 'launch') {
    /**
     * Launching, not paying. The step's work is getting the study out into the world, and money
     * and the freeze are both steps along the way — a study that paid and whose launch call then
     * failed has not finished this step, and the researcher has something left to do here.
     */
    return input.launched;
  }

  if (slug === 'fielding') {
    return FIELDING_ENDED.has(input.state);
  }

  if (slug === 'analyzing' || slug === 'brief') {
    return input.state === 'complete';
  }

  if (slug === 'study_type') {
    /*
      The column alone, with no lifecycle escape of the kind `launch` and `fielding` have. A study
      that is paid for, frozen or launched with no type would report this step unfinished for good,
      and a pick on it would be refused. No such study exists: every study is created on this step,
      and nothing can be paid for before its documents are drafted and approved.
    */
    return input.studyType !== null;
  }

  if (slug === 'materials') {
    return input.materials?.confirmedCurrent === true;
  }

  if (slug === 'goal') {
    return input.goal?.chosenCurrent === true;
  }

  return false;
};

/** One line of what a step settled, or null; see `isStudyStepComplete`. */
export const studyStepSummary = (slug: StudyStepSlug, input: StudyStepInput): string | null => {
  const kind = studyStepArtifact(slug);
  if (kind === 'rqs') {
    return questionsSummary(
      input.artifacts.find((artifact) => artifact.kind === kind),
      input.questions,
    );
  }

  if (kind === 'dg') {
    return guideSummary(
      input.artifacts.find((artifact) => artifact.kind === kind),
      input.guide,
    );
  }

  if (kind === 'audience') {
    return audienceSummary(
      input.artifacts.find((artifact) => artifact.kind === kind),
      input.audience,
    );
  }

  if (kind !== undefined) {
    return artifactSummary(input.artifacts.find((artifact) => artifact.kind === kind));
  }

  if (slug === 'launch') {
    return launchSummary(input);
  }

  if (slug === 'fielding') {
    if (!input.paid && !input.locked) {
      return null;
    }

    return FIELDING_SUMMARIES[input.state] ?? null;
  }

  if (slug === 'analyzing') {
    return ANALYZING_SUMMARIES[input.state] ?? null;
  }

  if (slug === 'study_type') {
    return studyTypeSummary(input.studyType);
  }

  if (slug === 'materials') {
    return materialsSummary(input.materials);
  }

  if (slug === 'goal') {
    return input.goal?.summary ?? null;
  }

  return null;
};

/**
 * The step a study's own lifecycle puts it at, or nothing while only its documents say where it is.
 *
 * A study's state is the stronger statement of the two. Money has changed hands, a conversation is
 * fielding, answers have been read — none of that is undone by wherever the researcher last looked,
 * so a study that is `ready` is at its Launch step, a launched one at Fielding and an analysed one
 * at Analyzing whatever the cursor says. A draft answers nothing here, because nothing about `draft` says which document is
 * being worked on; that is what the cursor and the approvals are for.
 */
export const lifecycleStudyStep = (
  input: Pick<StudyStepInput, 'state' | 'locked' | 'paid' | 'launched'>,
): StudyStepSlug | null => {
  if (input.state === 'complete') {
    return 'brief';
  }

  /**
   * Fieldwork is over and the answers are being read: Analyzing, ahead of the launched rule below,
   * which would otherwise keep the study on Fielding.
   */
  if (input.state === 'analyzing') {
    return 'analyzing';
  }

  /**
   * Fielding belongs to a study that has launched, and to no other. Money and the freeze both
   * land before Remesh is asked for anything, so a study that is paid for or frozen is still on
   * its Launch step being set up — and a launch that failed leaves it there indefinitely, which
   * is what splitting the lock from the launch is for. Sending such a study to Fielding would
   * draw a progress surface over a study nobody can be answering.
   *
   * A launched study stays here through every ending but the analysis, including the two that never
   * finished fielding as a success: their card is on that step.
   */
  if (input.launched) {
    return 'fielding';
  }

  /**
   * Two reasons to be at Launch and neither implies the other in principle: every document is
   * approved, or money and a freeze are already on the study. They coincide in every state the
   * product can currently produce — checkout only opens against a `ready` study and nothing takes
   * it back — so the second half is not exercised, and it is stated anyway because it is what
   * would keep this right if a paid study could ever stop being `ready`.
   */
  return input.state === 'ready' || input.paid || input.locked ? 'launch' : null;
};

/**
 * Which step opening this study lands on.
 *
 * The remembered step, unless the study's own lifecycle has gone past it — a finished study lands
 * on its Brief, an analysed one on Analyzing, a launched one on Fielding, one that is ready on
 * Launch. The cursor can lag: a
 * study can be launched from elsewhere, and a record of a move can simply fail to reach the
 * server. Landing on a step the study has demonstrably left would then be the product forgetting
 * what it has already done, which is worse than forgetting where somebody last clicked.
 */
export const landingStudyStep = (
  input: Pick<StudyStepInput, 'currentStep' | 'state' | 'locked' | 'paid' | 'launched'>,
): StudyStepSlug => {
  const lifecycle = lifecycleStudyStep(input);

  // A cursor on a step the study's state holds locked — Analyzing for a study whose analysis ended
  // under its quality floor — is not somewhere it can land: the shell would correct the address to
  // the very step it refuses. The lifecycle says where the study is instead.
  if (isHeldByState(input.currentStep, input.state)) {
    return lifecycle ?? FIRST_STUDY_STEP;
  }

  return lifecycle !== null && studyStepIndex(lifecycle) > studyStepIndex(input.currentStep)
    ? lifecycle
    : input.currentStep;
};

/**
 * How far this study has got, as an index into the ten — everything up to and including it can be
 * returned to.
 *
 * Three things say so and the furthest of them wins, because each knows something the others do
 * not. The cursor is where the researcher last was, and the step after it is open once that step
 * is settled. The lifecycle is what the study has actually done, and it is the one that stops a
 * study locking steps it has plainly been through: stepping back twice from Fielding must not put
 * Fielding out of reach. The approvals name every document that is finished, and the step after
 * the last of them is the next piece of work.
 *
 * Deriving from the cursor alone was the defect: reachability then moved backwards every time the
 * researcher did.
 */
const reachedIndex = (input: StudyStepInput): number => {
  const cursorIndex = studyStepIndex(input.currentStep);
  /**
   * Whether the study is done with the step its cursor names, which is what opens the one after
   * it. An unimplemented step has nothing on its screen to finish, so it is settled once every
   * built step before it is — holding the study behind it would be a dead end, but letting it
   * settle past an earlier built step that is unfinished would be a way round that step's gate.
   */
  const cursorSettled = studyStep(input.currentStep).implemented
    ? isStudyStepComplete(input.currentStep, input)
    : STUDY_STEPS.slice(0, cursorIndex).every(
        (step) => !step.implemented || isStudyStepComplete(step.slug, input),
      );

  /**
   * The lifecycle overlaps the approvals below rather than adding to them today: a study is
   * `ready` only once all three documents are approved, and the same lock and payment that put it
   * at Fielding are what make Launch complete. It is stated anyway, because it is the fact that
   * actually justifies the answer — a launched study can reach Fielding because it launched, not
   * because of how its Launch step happens to derive — and it is what keeps this right if that
   * derivation ever changes. A reader should not assume this term is exercised.
   */
  const lifecycle = lifecycleStudyStep(input);
  const reaches = [
    cursorSettled ? cursorIndex + 1 : cursorIndex,
    lifecycle === null ? 0 : studyStepIndex(lifecycle),
    ...STUDY_STEPS.map((step, index) =>
      step.implemented && isStudyStepComplete(step.slug, input) ? index + 1 : 0,
    ),
  ];

  // Unclamped: a step is compared against this with `<=`, so an answer past the last step means
  // the same as the last step, and a clamp would be a line nothing could ever observe.
  return Math.max(...reaches);
};

/**
 * Every step and where it stands for one study.
 *
 * Pure, and deliberately given a study's facts rather than a database handle: the same derivation
 * runs on the server, which stores the answer in the study read, and in the tests that pin it.
 */
export const deriveStudySteps = (input: StudyStepInput): StudyStepView[] => {
  const currentStep = landingStudyStep(input);
  const furthest = reachedIndex(input);

  return STUDY_STEPS.map((step, index) => {
    if (!step.implemented) {
      /*
        Unbuilt, and said so, once the study has reached it; locked like any other step until
        then. An unbuilt step that was always reachable was a door past every built step in front
        of it: a study could be moved onto it and, from there, on to whatever it opened.
      */
      return {
        slug: step.slug,
        name: step.name,
        about: step.about,
        status: index <= furthest ? ('unimplemented' as const) : ('locked' as const),
        summary: null,
      };
    }

    const status: StudyStepStatus = isStudyStepComplete(step.slug, input)
      ? 'complete'
      : isHeldByState(step.slug, input.state)
        ? 'locked'
        : step.slug === currentStep
          ? 'current'
          : index <= furthest
            ? 'available'
            : 'locked';

    return {
      slug: step.slug,
      name: step.name,
      about: step.about,
      status,
      summary: studyStepSummary(step.slug, input),
    };
  });
};

/**
 * Whether a study can be taken to a step right now.
 *
 * An unimplemented step the study has reached is reachable. Refusing to route to one would leave
 * Back and a deep link disagreeing about which steps exist, and the honest answer — a screen saying
 * the step is not built — is a better one than a redirect to somewhere the user did not ask for.
 * One the study has not reached is locked, as a built one would be.
 */
export const canReachStudyStep = (slug: StudyStepSlug, steps: readonly StudyStepView[]): boolean =>
  steps.find((step) => step.slug === slug)?.status !== 'locked';
